import { useState } from "react";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import CodeCard from "@/components/CodeCard";
import { Wand2, Plus, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const emptyLink = () => ({ title: "", url: "", notes: "" });
const emptySection = () => ({ title: "", links: [emptyLink()] });

const T = {
  en: {
    title: "llms.txt Generator",
    sub: "Create an llms.txt file - the emerging standard that tells AI assistants exactly what your site is about and where the important content lives.",
    whatIs: "What is llms.txt and why should I add one?",
    what1a: "is a proposed standard (llmstxt.org) - a markdown file placed at",
    what1b: "that gives large language models a concise, curated map of your site.",
    what2: "While robots.txt controls whether AI can crawl you, llms.txt controls what they understand: your site's purpose, key docs, and the pages you most want AI assistants to read and cite.",
    what3: "It uses simple markdown: an H1 title, a blockquote summary, and H2 sections containing annotated links.",
    siteInfo: "Site information",
    fName: "Site name *",
    fDesc: "One-line description",
    fDescPh: "Developer platform for building payment integrations",
    descHint: "Becomes the blockquote summary AI models read first",
    fDetails: "Additional context (optional)",
    fDetailsPh: "Important notes about your site, target audience, or how to interpret the docs...",
    sections: "Content sections",
    addSection: "Add section",
    sectionTitle: "Section title (e.g. Docs, Guides, API)",
    linkTitlePh: "Page title",
    linkNotesPh: "Short note about this page (optional)",
    addLink: "Add link",
    generate: "Generate llms.txt",
    generating: "Generating...",
    nameRequired: "Site name is required",
    generated: "llms.txt generated",
    emptyBody: "Your llms.txt preview will appear here. Upload the finished file to your site root at",
  },
  fr: {
    title: "Générateur llms.txt",
    sub: "Créez un fichier llms.txt - le standard émergent qui indique aux assistants IA de quoi parle votre site et où se trouve le contenu important.",
    whatIs: "Qu'est-ce que llms.txt et pourquoi en ajouter un?",
    what1a: "est un standard proposé (llmstxt.org) - un fichier markdown placé à",
    what1b: "qui donne aux modèles de langage une carte concise et organisée de votre site.",
    what2: "robots.txt contrôle si les IA peuvent vous explorer; llms.txt contrôle ce qu'elles comprennent : la mission de votre site, les documents clés et les pages que vous voulez le plus voir lues et citées par les assistants IA.",
    what3: "Il utilise du markdown simple : un titre H1, un résumé en citation, et des sections H2 contenant des liens annotés.",
    siteInfo: "Informations du site",
    fName: "Nom du site *",
    fDesc: "Description en une ligne",
    fDescPh: "Plateforme pour développeurs d'intégrations de paiement",
    descHint: "Devient le résumé que les modèles IA lisent en premier",
    fDetails: "Contexte additionnel (optionnel)",
    fDetailsPh: "Notes importantes sur votre site, votre public cible, ou comment interpréter la documentation...",
    sections: "Sections de contenu",
    addSection: "Ajouter une section",
    sectionTitle: "Titre de la section (ex. Docs, Guides, API)",
    linkTitlePh: "Titre de la page",
    linkNotesPh: "Courte note sur cette page (optionnel)",
    addLink: "Ajouter un lien",
    generate: "Générer llms.txt",
    generating: "Génération...",
    nameRequired: "Le nom du site est requis",
    generated: "llms.txt généré",
    emptyBody: "L'aperçu de votre llms.txt apparaîtra ici. Téléversez le fichier final à la racine de votre site à",
  },
};

export default function LlmsGenerator() {
  const { lang } = useLang();
  const t = T[lang];
  const [siteName, setSiteName] = useState("");
  const [description, setDescription] = useState("");
  const [details, setDetails] = useState("");
  const [sections, setSections] = useState([{ title: "Docs", links: [emptyLink()] }]);
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  const setSection = (idx, patch) => {
    setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, ...patch } : sec)));
  };
  const setLink = (sIdx, lIdx, patch) => {
    setSections((s) =>
      s.map((sec, i) =>
        i === sIdx ? { ...sec, links: sec.links.map((l, j) => (j === lIdx ? { ...l, ...patch } : l)) } : sec
      )
    );
  };

  const generate = async () => {
    if (!siteName.trim()) {
      toast.error(t.nameRequired);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/generate/llms-txt", { siteName, description, details, sections });
      setOutput(data.content);
      toast.success(t.generated);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-muted-foreground">{t.sub}</p>
      </div>

      <Accordion type="single" collapsible className="mt-6 max-w-2xl">
        <AccordionItem value="what" className="border rounded-lg px-4 bg-card">
          <AccordionTrigger className="text-sm hover:no-underline" data-testid="llms-explainer-trigger">{t.whatIs}</AccordionTrigger>
          <AccordionContent className="text-sm text-muted-foreground space-y-2">
            <p>
              <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">llms.txt</span> {t.what1a}
              <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded mx-1">https://votresite.com/llms.txt</span>
              {t.what1b}
            </p>
            <p>{t.what2}</p>
            <p>{t.what3}</p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-6 h-fit">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">{t.siteInfo}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="llms-name">{t.fName}</Label>
              <Input id="llms-name" value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="Acme Docs" data-testid="llms-site-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="llms-desc">{t.fDesc}</Label>
              <Input id="llms-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t.fDescPh} data-testid="llms-description-input" />
              <p className="text-xs text-muted-foreground">{t.descHint}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="llms-details">{t.fDetails}</Label>
              <Textarea id="llms-details" value={details} onChange={(e) => setDetails(e.target.value)} rows={3} placeholder={t.fDetailsPh} data-testid="llms-details-input" />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <Label>{t.sections}</Label>
              <Button variant="outline" size="sm" onClick={() => setSections((s) => [...s, emptySection()])} data-testid="llms-add-section-button">
                <Plus className="h-3.5 w-3.5 mr-1" /> {t.addSection}
              </Button>
            </div>

            {sections.map((section, sIdx) => (
              <div key={sIdx} className="rounded-md border p-4 space-y-3 relative">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7 text-muted-foreground"
                  onClick={() => setSections((s) => s.filter((_, i) => i !== sIdx))}
                  aria-label="Remove section"
                  data-testid={`llms-remove-section-${sIdx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t.sectionTitle}</Label>
                  <Input value={section.title} onChange={(e) => setSection(sIdx, { title: e.target.value })} placeholder="Docs" data-testid={`llms-section-${sIdx}-title`} />
                </div>
                {section.links.map((link, lIdx) => (
                  <div key={lIdx} className="rounded-md bg-muted/50 p-3 space-y-2 relative">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-1 right-1 h-6 w-6 text-muted-foreground"
                      onClick={() => setSection(sIdx, { links: section.links.filter((_, j) => j !== lIdx) })}
                      aria-label="Remove link"
                      data-testid={`llms-remove-link-${sIdx}-${lIdx}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={link.title} onChange={(e) => setLink(sIdx, lIdx, { title: e.target.value })} placeholder={t.linkTitlePh} className="bg-card" data-testid={`llms-link-${sIdx}-${lIdx}-title`} />
                      <Input value={link.url} onChange={(e) => setLink(sIdx, lIdx, { url: e.target.value })} placeholder="https://..." className="bg-card" data-testid={`llms-link-${sIdx}-${lIdx}-url`} />
                    </div>
                    <Input value={link.notes} onChange={(e) => setLink(sIdx, lIdx, { notes: e.target.value })} placeholder={t.linkNotesPh} className="bg-card" data-testid={`llms-link-${sIdx}-${lIdx}-notes`} />
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setSection(sIdx, { links: [...section.links, emptyLink()] })} data-testid={`llms-add-link-${sIdx}-button`}>
                  <Plus className="h-3 w-3 mr-1" /> {t.addLink}
                </Button>
              </div>
            ))}

            <Button onClick={generate} disabled={loading} className="w-full" data-testid="llms-generate-button">
              <Wand2 className="h-4 w-4 mr-2" />
              {loading ? t.generating : t.generate}
            </Button>
          </CardContent>
        </Card>

        <div className="lg:col-span-6">
          {output ? (
            <CodeCard
              title="llms.txt"
              language="markdown"
              code={output}
              filename="llms.txt"
              testIdPrefix="llms-output"
              maxHeight="640px"
            />
          ) : (
            <div className="rounded-lg border border-dashed p-10 text-center h-full flex flex-col items-center justify-center min-h-[300px]" data-testid="llms-empty-state">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <FileText className="h-6 w-6" />
              </div>
              <p className="mt-3 text-sm text-muted-foreground max-w-sm">
                {t.emptyBody} <span className="font-mono text-xs">/llms.txt</span>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
